import {
	APPOINTMENTS_INDIVIDUAL_CONTACTS_FETCHED,
	INDIVIDUAL_CONTACT_ARCHIVE_STATUS_CHANGED,
	INDIVIDUAL_CONTACT_CREATED,
	INDIVIDUAL_CONTACT_EDITED,
	INDIVIDUAL_CONTACT_FETCHED,
	INDIVIDUAL_CONTACTS_FETCHED
} from "./actionType";
import {Dispatch} from "redux";
import {api} from "../../../api/api";
import {Action} from "redux-actions";
import IndividualContact from "../../../model/contact/IndividualContact";
import IndividualContactData from "../../../model/contact/individual/IndividualContactData";
import {OnFailure, OnSuccess, OnSuccessData} from "../actionHelpers";
import {none, option, Option} from "ts-option";
import Address from "../../../model/address/Address";
import SiteData from "../../../model/site/SiteData";
import {createSite, fetchSite} from "../site/site";
import {ContactsMap} from "../../reducer/contact/ContactsState";
import {notifyBugsnagAndShowToast} from "../../../utils/Utils";
import {fetchPartnerBrand, fetchPBrandPaymentsStatus} from "../partnerBrand/partnerBrand";

/**
 * Individual contact creation successful action.
 * @param {string} contact Created contact.
 * @returns {Action<INDIVIDUAL_CONTACT_CREATED>}
 */
export function individualContactCreated(contact: IndividualContact): Action<INDIVIDUAL_CONTACT_CREATED> {
	return {type: INDIVIDUAL_CONTACT_CREATED, payload: {contact}};
}

/**
 * Individual contacts fetched action.
 * @returns {Action<INDIVIDUAL_CONTACTS_FETCHED>}
 */
export function individualContactsFetched(contacts: ContactsMap): Action<INDIVIDUAL_CONTACTS_FETCHED> {
	return {type: INDIVIDUAL_CONTACTS_FETCHED, payload: {contacts}};
}

/**
 * Individual contacts fetched action.
 * @returns {Action<INDIVIDUAL_CONTACTS_FETCHED>}
 */
export function individualContactFetched(contact: IndividualContact): Action<INDIVIDUAL_CONTACT_FETCHED> {
	return {type: INDIVIDUAL_CONTACT_FETCHED, payload: {contact}};
}

/**
 * Individual contact edition successful action.
 * @param {string} contact Edited contact.
 * @returns {Action<INDIVIDUAL_CONTACT_EDITED>}
 */
export function individualContactEdited(contact: IndividualContact): Action<INDIVIDUAL_CONTACT_EDITED> {
	return {type: INDIVIDUAL_CONTACT_EDITED, payload: {contact}};
}


/**
 * Individual contact archive flag value change successful action.
 * @returns {Action<INDIVIDUAL_CONTACT_ARCHIVE_STATUS_CHANGED>}
 * @param contactId
 * @param archived
 */
export function individualContactArchiveStatusUpdated(contactId: string,
	archived: boolean): Action<INDIVIDUAL_CONTACT_ARCHIVE_STATUS_CHANGED> {
	return {type: INDIVIDUAL_CONTACT_ARCHIVE_STATUS_CHANGED, payload: {contactId, archived}};
}

/**
 * Individual contacts fetched action.
 * @returns {Action<INDIVIDUAL_CONTACTS_FETCHED>}
 */
export function appointmentsIndividualContactsFetched(contacts: ContactsMap): Action<APPOINTMENTS_INDIVIDUAL_CONTACTS_FETCHED> {
	return {type: APPOINTMENTS_INDIVIDUAL_CONTACTS_FETCHED, payload: {contacts}};
}

/**
 * Create individual contact action.
 */
export function createIndividualContact(contact: IndividualContactData,
	onSuccess: OnSuccessData<IndividualContact>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.contactApi.createIndividualContact(contact).then((contact: IndividualContact) => {
			dispatch(individualContactCreated(contact));
			onSuccess(contact);
		}).catch(onFailure);

	}
}

/**
 * Fetch all individual contacts.
 */
export function fetchIndividualContacts(onSuccess: OnSuccessData<ContactsMap>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.contactApi.fetchIndividualContacts().then((contacts: ContactsMap) => {
			onSuccess(contacts);
			dispatch(individualContactsFetched(contacts))
		}).catch(onFailure);
	}
}

/**
 * Fetch individual contact.
 */
export function fetchIndividualContact(contactId: string, onSuccess: OnSuccessData<IndividualContact>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.contactApi.fetchIndividualContact(contactId).then((contact: IndividualContact) => {
			dispatch(individualContactFetched(contact));
			contact.data.primarySiteId.map(
				sId => dispatch(fetchSite(sId, () => {}, err => notifyBugsnagAndShowToast(err, "Fetch Site"))));
			contact.data.partnerBrandId.map(pbId => {
				dispatch(
					fetchPBrandPaymentsStatus(pbId, (e) => notifyBugsnagAndShowToast(e, "Fetch partner brand payments status")));
				dispatch(fetchPartnerBrand(pbId, (e) => notifyBugsnagAndShowToast(e, "Fetch partner brand")))
			});
			onSuccess(contact);
		}).catch(onFailure);
	}
}

/**
 * Edit individual contact.
 */
export function editIndividualContact(contact: IndividualContact,
	address: Option<Address>,
	removeSiteOnEmptyAddress: boolean,
	onSuccess: OnSuccessData<IndividualContact>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {

		const updateContact = (siteId: Option<string>) => {
			const updatedContactData = contact.data.copy({primarySiteId: siteId});
			api.instance.contactApi.editIndividualContact(contact.id, updatedContactData).then(contact => {
				dispatch(individualContactEdited(contact));
				onSuccess(contact);
			}).catch(onFailure);
		};

		if (address.isDefined) {
			dispatch(
				createSite(new SiteData(address.get), (site) => updateContact(option(site.id)), () => onFailure)
			)
		}
		else {
			updateContact(removeSiteOnEmptyAddress ? none : contact.data.primarySiteId);
		}
	}
}

/**
 * Update "archived" flag in contact.
 * @param {string} contactId
 * @param {boolean} archived
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function updateArchivedStatus(contactId: string,
	archived: boolean,
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.contactApi.changeContactArchivedStatus(contactId, archived).then(() => {
			dispatch(individualContactArchiveStatusUpdated(contactId, archived));
			onSuccess()
		}).catch(onFailure);
	}
}
